<template>
  <div :class="`lead-card ${variant}`" v-if="!loading">
    <h3 v-if="title">{{ title }}</h3>
    <div class="table-layout">
      <div class="table-layout-no-data" v-if="!filteredItems || filteredItems.length === 0">
        <span>{{ $t('leads.detail.no-data') }}</span>
      </div>
      <template v-for="{text, value, id, type, loading, separator, assigneeData} in filteredItems">
        <div class="table-layout-item" :key="id">
          <label-tooltip max-width="100%">
            <div class="table-layout-item-header">
              {{ text }}
            </div>
          </label-tooltip>
          <label-tooltip max-width="100%" v-if="!loading && type !== 'assignee'">
            <div v-if="type === 'text'" class="table-layout-item-value" v-html="value"></div>
            <div v-else-if="type === 'date'" class="table-layout-item-value">
              {{ getCreatedAtDate(value) }}
            </div>
            <m-phone-label
              v-else-if="type === 'phone'"
              :value="value"
              class="table-layout-item-value"
              :country="country" />
            <button v-else-if="type === 'link'" @click="downloadFile(value.url, value.fileName)" class="link">
              {{ value.text }}
            </button>
          </label-tooltip>
          <div v-else-if="!loading && type === 'assignee'" class="table-layout-item-value" @click.stop>
            <slot name="assignee" :data="assigneeData">
              <span>{{ value }}</span>
            </slot>
          </div>
          <v-skeleton-loader v-else type="list-item" class="skeleton-item" />
        </div>
        <v-divider v-if="separator" :key="`separator-${id}`" class="table-separator" />
      </template>
    </div>
  </div>
  <div v-else :class="`skeleton-card ${variant}`">
    <v-sheet color="lead-transparent">
      <v-skeleton-loader
        type="heading, list-item-two-line, list-item-three-line, list-item-two-line"></v-skeleton-loader>
    </v-sheet>
  </div>
</template>

<script lang="ts">
import {Country} from '@mapit/common-utils/dist/types'
import {computed, defineComponent, onMounted, PropType, ref} from '@vue/composition-api'
import {TranslateResult} from 'vue-i18n'

import LabelTooltip from '@/components/text/LabelTooltip.vue'
import {toDateFormat} from '@/utils/date-helper'
import {mapGetters} from '@/utils/map-store'

type LeadDetailItemType = 'text' | 'phone' | 'date' | 'link' | 'assignee'

interface LeadDetailItem {
  text?: string
  value?: any
  id?: string
  type?: LeadDetailItemType
  loading?: boolean
  separator?: boolean
  hidden?: boolean
  assigneeData?: any
}

export default defineComponent({
  name: 'LeadDetailCard',
  components: {LabelTooltip},
  props: {
    items: {
      type: Array as PropType<LeadDetailItem[]>,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String as PropType<string | TranslateResult>,
      required: false,
    },
    variant: {
      type: String as PropType<'outlined' | 'filled'>,
      default: 'filled',
    },
    country: {
      type: String as PropType<Country>,
      required: false,
      default: Country.Spain,
    },
  },
  setup(props, {root}) {
    const windowWidth = ref(0)

    const {getLocale: locale} = mapGetters(root.$store.getters)

    const filteredItems = computed(() => {
      return props.items.filter(item => item.value !== undefined && !item.hidden)
    })

    onMounted(() => {
      windowWidth.value = window.innerWidth

      root.$nextTick(() => {
        window.addEventListener('resize', onResize)
      })
    })

    function onResize() {
      windowWidth.value = window.innerWidth
    }

    function getCreatedAtDate(created: any) {
      return toDateFormat(created, locale.value)
    }

    async function downloadFile(url: string, name: string) {
      const pdf = await fetch(url)
      const pdfBlob = await pdf.blob()
      const pdfUrl = URL.createObjectURL(pdfBlob)

      const link = document.createElement('a')
      link.href = pdfUrl
      link.download = name

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(pdfUrl)
    }

    return {
      filteredItems,
      windowWidth,
      getCreatedAtDate,
      downloadFile,
    }
  },
})
</script>
<style lang="scss">
// Override v-skeleton styles
.lead-card {
  font-family: 'ProximaNovaRegular', sans-serif;
  font-size: 15px;

  %status {
    content: '';
    width: 10px;
    min-width: 10px;
    height: 10px;
    margin-right: 10px;
    border-radius: 100%;
  }

  .status {
    display: flex;
    align-items: center;

    &-new:before {
      @extend %status;
      background: #fec478;
    }

    &-in_progress:before {
      @extend %status;
      background: #49bcf4;
    }

    &-with_offer:before {
      @extend %status;
      background: #48a44a;
    }

    &-lost:before {
      @extend %status;
      background: #bdbdbd;
    }
  }

  .skeleton-item {
    .v-skeleton-loader__list-item.v-skeleton-loader__bone {
      background: transparent !important;
      width: 200px !important;
      padding: 0 !important;
      margin: 0 !important;
      height: 22px;
    }
  }
}

.skeleton-card {
  .v-skeleton-loader__list-item-three-line,
  .v-skeleton-loader__list-item-two-line {
    background: transparent !important;
    padding: 0 !important;
  }
}
</style>
<style scoped lang="scss">
.link {
  appearance: none;
  text-decoration: underline !important;
  font: normal normal normal 16px/26px 'ProximaNovaRegular' !important;
  text-transform: inherit !important;
  padding: inherit !important;
  margin: inherit !important;
  line-height: inherit !important;
  height: auto !important;
  text-align: center;

  color: #3797f0 !important;
  opacity: 1;
  &:hover {
    cursor: pointer;
  }
}
h3 {
  color: #dc182d;
  text-transform: uppercase;
  font-family: 'ProximaNovaExCnSBold', sans-serif;
  font-size: 24px;
}

.lead-transparent {
  background: transparent !important;
}

.lead-card {
  padding: 28px;
  border-radius: 6px;
  width: 100%;

  &.filled {
    background: #f2f2f2;
  }

  .table-separator {
    grid-column: 1 / -1;
    margin: 0.5rem 0 !important;
  }

  .skeleton-item {
    grid-column: 2;
  }

  .table {
    &-layout {
      display: grid;
      grid-template-columns: minmax(15%, 30%) minmax(0, 1fr);
      row-gap: 5px; /* row spacing */
      column-gap: 8px;

      &-no-data {
        grid-column: 1 / -1;
        text-align: center;
      }

      &-item {
        display: contents;

        &-header {
          grid-column: 1;
          font-weight: normal;
          color: #bdbdbd;
          font-family: 'ProximaNovaRegular', sans-serif;
        }

        &-value {
          grid-column: 2;
          font-family: 'ProximaNovaRegular', sans-serif;
          color: #222222;
        }
      }
    }
  }

  &.outlined {
    padding: 0;
    background: transparent;

    .table-layout {
      padding: 28px 20px;
      border-radius: 6px;
      border: 1px rgba(0, 0, 0, 0.12) solid;

      .table-layout-item .table-layout-item-value {
        font-family: 'ProximaNovaSemibold', sans-serif;
        color: #222222;
      }
    }
  }
}

.skeleton-card {
  min-height: 250px;
  background: #f2f2f2;
  border-radius: 6px;
  padding: 28px;
  width: 100%;

  &.filled {
    background: #f2f2f2;
  }

  &.outlined {
    background: transparent;
    border-radius: 6px;
    border: 1px rgba(0, 0, 0, 0.12) solid;
  }
}
</style>
